// ui-controls.js — wires the control panel (#control-col) to parameters + tree.
//
// setupControls() attaches the slider/select listeners declared in index.html:
// trunk height/thickness/curvature/color and leaf type/density/size/color all
// mutate the shared `parameters` and update the live tree in real time. It also
// wires the Shape-mode toggle button to interaction's toggleShapeMode.
//
// The updateFoliage* helpers walk the current tree (app.tree) and its main
// branches to re-apply foliage density/size after a slider change.

import { parameters, app } from './core.js';
import { toggleShapeMode, togglePruneMode, refreshModeButtons } from './interaction.js';
import { playSfx } from './audio.js';

// Slider-drag SFX: while a size slider is dragged, play a throttled sound that
// scales with the tree's size (the slider's normalized position) and is quieter
// on the way down, so shrinking is a soft settle. Trunk height/thickness use the
// grow sound; foliage density/size use the steady shaping rustle. Each profile
// keeps its own throttle so the rapid 'input' stream can't machine-gun the sample.
const SLIDER_SFX = {
  truncHeight:    { sound: 'grow',   ms: 520, rate: 0.8, downVol: 0.5, minVol: 0.55 },
  truncThickness: { sound: 'grow',   ms: 520, rate: 0.8, downVol: 0.5, minVol: 0.55 },
  foliageDensity: { sound: 'rustle', ms: 650, rate: 0.7, downVol: 0.5, minVol: 0.55 },
  foliageSize:    { sound: 'rustle', ms: 650, rate: 0.7, downVol: 0.5, minVol: 0.55 },
};
const lastSliderSfx = {};  // profile id -> performance.now() of its last sound
const lastSliderVal = {};  // slider id -> previous value, to detect up vs down

// Play the configured drag SFX for a size slider (no-op if none configured or the
// value didn't change). `el` is the range input (read for its min/max).
function playSliderSfx(id, el, value, prev) {
  const cfg = SLIDER_SFX[id];
  if (!cfg || prev === undefined || value === prev) return;
  const now = performance.now();
  if (now - (lastSliderSfx[id] || 0) < cfg.ms) return;
  lastSliderSfx[id] = now;
  const min = parseFloat(el.min) || 0;
  const max = parseFloat(el.max);
  const norm = max > min ? (value - min) / (max - min) : 1; // 0 = smallest, 1 = biggest
  const sizeVol = cfg.minVol + (1 - cfg.minVol) * norm;
  const dirVol = value > prev ? 1 : cfg.downVol;
  playSfx(cfg.sound, { rate: cfg.rate, volume: sizeVol * dirVol });
}

// Set the `--fill` custom property on a range input to the percentage of its
// current value, so the CSS colored-track follows the thumb in WebKit (which
// has no native filled track). Safe to call on any element; no-ops on non-ranges.
function updateSliderFill(slider) {
  if (!slider || slider.type !== 'range') return;
  const min = parseFloat(slider.min) || 0;
  const max = parseFloat(slider.max);
  const pct = max > min ? ((parseFloat(slider.value) - min) / (max - min)) * 100 : 0;
  slider.style.setProperty('--fill', pct + '%');
}

// Write the current `parameters` values back into the control inputs so the UI
// reflects the source of truth, overriding any stale browser-restored values.
// Exported so gallery.js can reuse it when loading/receiving a tree (single
// source of truth for the full slider+select set, instead of a partial copy).
export function syncControlsFromParameters() {
  const setSlider = (id, value) => {
    const slider = document.getElementById(id);
    const display = document.getElementById(id + 'Value');
    if (slider) { slider.value = value; updateSliderFill(slider); }
    if (display) display.textContent = value;
  };

  setSlider('truncHeight', parameters.truncHeight);
  setSlider('truncThickness', parameters.truncThickness);
  setSlider('truncNoise', parameters.truncNoise);
  setSlider('truncCurve', parameters.truncCurve);
  setSlider('foliageDensity', parameters.foliageDensity);
  setSlider('foliageSize', parameters.foliageSize);

  const setSelect = (id, value) => {
    const select = document.getElementById(id);
    if (select) select.value = value;
  };

  setSelect('leafType', parameters.leafType);
  setSelect('foliageColor', parameters.foliageColor);
  // truncColor is stored as a number, but the <option> values are "0x…" strings.
  setSelect('truncColor', '0x' + parameters.truncColor.toString(16).toUpperCase().padStart(6, '0'));
}

function setupControls() {
  // Browsers restore the previous session's form values on reload, which then
  // disagree with the fresh `parameters` defaults the first tree is built from
  // (the HTML defaults are also slightly out of sync). Push parameters -> DOM
  // up front so the controls always reflect the actual tree.
  syncControlsFromParameters();

  // Setup all control listeners
  const controlIds = ['truncHeight', 'truncThickness', 'truncNoise', 'truncCurve'];
  
  controlIds.forEach(id => {
    const slider = document.getElementById(id);
    const valueDisplay = document.getElementById(id + 'Value');
    
    slider.addEventListener('input', (e) => {
      const value = parseFloat(e.target.value);
      parameters[id] = value;
      valueDisplay.textContent = e.target.value;

      // Soft grow sound while dragging height/thickness (see playSliderSfx).
      playSliderSfx(id, e.target, value, lastSliderVal[id]);
      lastSliderVal[id] = value;

      // Reshape the trunk (and re-attach branches) live as the slider drags.
      if (app.tree && app.tree.updateTrunkGeometry) {
        app.tree.updateTrunkGeometry();
      }
    });

    valueDisplay.textContent = slider.value;
  });

  // SPECIAL HANDLING for foliageDensity - update leaves in real time
  const foliageDensitySlider = document.getElementById('foliageDensity');
  const foliageDensityValue = document.getElementById('foliageDensityValue');
  
  foliageDensitySlider.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    parameters.foliageDensity = value;
    foliageDensityValue.textContent = e.target.value;

    // Rustle while dragging foliage density (see playSliderSfx).
    playSliderSfx('foliageDensity', e.target, value, lastSliderVal.foliageDensity);
    lastSliderVal.foliageDensity = value;

    // Update foliage density in real time
    if (app.tree) {
      updateFoliageDensity();
    }
  });
  foliageDensityValue.textContent = foliageDensitySlider.value;

  // SPECIAL HANDLING for foliageSize - update leaves in real time  
  const foliageSizeSlider = document.getElementById('foliageSize');
  const foliageSizeValue = document.getElementById('foliageSizeValue');
  
  foliageSizeSlider.addEventListener('input', (e) => {
    const value = parseFloat(e.target.value);
    parameters.foliageSize = value;
    foliageSizeValue.textContent = e.target.value;

    // Rustle while dragging foliage size (see playSliderSfx).
    playSliderSfx('foliageSize', e.target, value, lastSliderVal.foliageSize);
    lastSliderVal.foliageSize = value;

    // Update foliage size in real time
    if (app.tree) {
      updateFoliageSize();
    }
  });
  foliageSizeValue.textContent = foliageSizeSlider.value;

  // ADD/RESTORE leaf type selector
  const leafTypeSelect = document.getElementById('leafType');
  if (leafTypeSelect) {
    leafTypeSelect.addEventListener('change', (e) => {
      const prevLeafType = parameters.leafType;
      parameters.leafType = e.target.value;

      // Cherry blossoms should bloom pink the moment they're selected. When first
      // switching into cherry, snap the foliage color to "Pink" (the soft sakura
      // palette) so the petals never inherit a stale non-pink color and the
      // dropdown visibly matches the bloom. Done before the rebuild below so the
      // new foliage picks up the pink palette.
      if (e.target.value === 'cherry' && prevLeafType !== 'cherry') {
        parameters.foliageColor = 'pinks';
        const fc = document.getElementById('foliageColor');
        if (fc) fc.value = 'pinks';
      }

      // Update all existing foliage
      if (app.tree && app.tree.branches) {
        app.tree.branches.forEach(branch => {
          if (branch.updateLeafType) {
            branch.updateLeafType();
          }
        });
      }
      
      // Update sub-branches on main branches
      if (app.tree && app.tree.mesh) {
        app.tree.mesh.children.forEach(child => {
          if (child.userData && child.userData.isMainBranch) {
            app.tree.updateSubBranchFoliage(child);
          }
        });
      }
    });
  }

  // Trunk color selector
  document.getElementById('truncColor').addEventListener('change', (e) => {
    parameters.truncColor = parseInt(e.target.value);
    if (app.tree && app.tree.updateTrunkColor) {
      app.tree.updateTrunkColor();
    }
  });

  // Foliage color selector - UPDATED to handle both needles and leaves
  document.getElementById('foliageColor').addEventListener('change', (e) => {
    parameters.foliageColor = e.target.value;
    
    // Update regular branches. Conifer types (pine + candle long needles + juniper
    // scale foliage + larch rosettes + snowy evergreen) recolor their instanced
    // needles/cords; every other type recolors instanced leaves. Mirrors isNeedleType
    // (kept inline to avoid the import here).
    const needled = parameters.leafType === "pine" || parameters.leafType === "candle"
      || parameters.leafType === "juniper" || parameters.leafType === "larch"
      || parameters.leafType === "evergreen";
    if (app.tree && app.tree.branches) {
      app.tree.branches.forEach(branch => {
        if (needled && branch.updateNeedleColors) {
          branch.updateNeedleColors();
        } else if (!needled && branch.updateLeafColors) {
          branch.updateLeafColors();
        }
      });
    }
    
    // Update sub-branches on main branches
    if (app.tree && app.tree.mesh) {
      app.tree.mesh.children.forEach(child => {
        if (child.userData && child.userData.isMainBranch) {
          app.tree.updateSubBranchFoliageColors(child);
        }
      });
    }

    // Pine cones key off the colour too: they vanish on Spring-Green and turn fresh
    // green on the plain Green palette, mature brown otherwise — so rebuild them.
    if (app.tree && app.tree.refreshPineCones) app.tree.refreshPineCones();
  });

  // Shape / Prune are mutually exclusive mode toggles (Prune is the default).
  const shapeModeButton = document.getElementById('shapeModeToggle');
  if (shapeModeButton) {
    shapeModeButton.addEventListener('click', toggleShapeMode);
  }
  const pruneModeButton = document.getElementById('pruneModeToggle');
  if (pruneModeButton) {
    pruneModeButton.addEventListener('click', togglePruneMode);
  }
  // Set the initial active/grayed-out look (Prune is the default mode).
  refreshModeButtons();

  // Mobile: while a slider is actively dragged, mark #control-col so the CSS can
  // fade the bottom sheet to near-transparent — letting the user watch the tree
  // reshape live behind it. Pointer events cover both touch and mouse; the
  // release is listened for on window so it still clears if the finger lifts
  // outside the thumb.
  const controlCol = document.getElementById('control-col');
  const controlsPanel = document.getElementById('controls');
  if (controlCol && controlsPanel) {
    // Keep every slider's colored fill in sync with its thumb as it drags.
    controlsPanel.querySelectorAll('input[type="range"]').forEach(updateSliderFill);
    controlsPanel.addEventListener('input', (e) => updateSliderFill(e.target));

    controlsPanel.addEventListener('pointerdown', (e) => {
      if (e.target.matches('input[type="range"]')) {
        controlCol.classList.add('control-dragging');
        // Tag the dragged row so CSS can keep its label + slider visible while
        // everything else in the sheet fades to the background.
        const row = e.target.closest('.control-row');
        if (row) row.classList.add('dragging-active');
      }
    });
    const endDrag = () => {
      controlCol.classList.remove('control-dragging');
      controlsPanel.querySelectorAll('.control-row.dragging-active')
        .forEach(row => row.classList.remove('dragging-active'));
    };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  }

}

function updateFoliageDensity() {
  
  // Update regular branches
  if (app.tree && app.tree.branches) {
    app.tree.branches.forEach(branch => {
      if (branch.updateFoliageDensity) {
        branch.updateFoliageDensity();
      }
    });
  }
  
  // Update main branches and their sub-branches
  if (app.tree && app.tree.mesh) {
    app.tree.mesh.children.forEach(child => {
      if (child.userData && child.userData.isMainBranch) {
        updateMainBranchFoliageDensity(child);
      }
    });
  }
}

function updateFoliageSize() {
  
  // Update regular branches
  if (app.tree && app.tree.branches) {
    app.tree.branches.forEach(branch => {
      if (branch.updateFoliageSize) {
        branch.updateFoliageSize();
      }
    });
  }
  
  // Update main branches and their sub-branches
  if (app.tree && app.tree.mesh) {
    app.tree.mesh.children.forEach(child => {
      if (child.userData && child.userData.isMainBranch) {
        updateMainBranchFoliageSize(child);
      }
    });
  }
}

function updateMainBranchFoliageDensity(mainBranchGroup) {
  const updateDensityRecursively = (object) => {
    if (object.userData && object.userData.branch) {
      const branch = object.userData.branch;
      if (branch.updateFoliageDensity) {
        branch.updateFoliageDensity();
      }
    }
    
    if (object.children) {
      object.children.forEach(child => updateDensityRecursively(child));
    }
  };
  
  updateDensityRecursively(mainBranchGroup);
}

function updateMainBranchFoliageSize(mainBranchGroup) {
  const updateSizeRecursively = (object) => {
    if (object.userData && object.userData.branch) {
      const branch = object.userData.branch;
      if (branch.updateFoliageSize) {
        branch.updateFoliageSize();
      }
    }
    
    if (object.children) {
      object.children.forEach(child => updateSizeRecursively(child));
    }
  };
  
  updateSizeRecursively(mainBranchGroup);
}

export { setupControls };
